import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import type {
  WhatsAppBroadcastAnalytics,
  WhatsAppBroadcastRecipient,
} from "../types";
import { BroadcastBoardColumn } from "./BroadcastBoardColumn";
import { BroadcastContactQuickViewModal } from "./BroadcastContactQuickViewModal";
import { bucketRecipients } from "./broadcastBoard";

const IN_FLIGHT_STATUSES = new Set(["accepted", "queued", "sending"]);

export function BroadcastBoardView({
  recipients,
  analytics,
  loading,
}: {
  recipients: WhatsAppBroadcastRecipient[];
  analytics: WhatsAppBroadcastAnalytics | null;
  loading: boolean;
}) {
  const [selected, setSelected] = useState<WhatsAppBroadcastRecipient | null>(
    null,
  );

  if (loading && recipients.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 size-4 animate-spin" />
        Loading recipients…
      </div>
    );
  }

  if (recipients.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
        No recipients yet.
      </div>
    );
  }

  const columns = bucketRecipients(recipients);
  const eligibleTotal = analytics?.eligible_count ?? recipients.length;
  const inFlight = recipients.some((recipient) =>
    IN_FLIGHT_STATUSES.has(
      String(recipient.provider_status ?? recipient.status),
    ),
  );
  const truncated =
    typeof analytics?.recipient_count === "number" &&
    analytics.recipient_count > recipients.length;

  return (
    <div className="space-y-3">
      {inFlight ? (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>Still being delivered</AlertTitle>
          <AlertDescription>
            Some messages are still in flight — the later columns will keep
            filling as delivery and read receipts arrive. Refresh for the
            latest.
          </AlertDescription>
        </Alert>
      ) : null}

      {truncated ? (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>
            Showing the first {recipients.length} recipients
          </AlertTitle>
          <AlertDescription>
            This broadcast has {analytics?.recipient_count} recipients, more
            than the board fetch limit. Column percentages are accurate from the
            analytics totals; some cards are omitted.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex gap-3 overflow-x-auto pb-4">
        {columns.map((column) => (
          <BroadcastBoardColumn
            key={column.key}
            column={column}
            eligibleTotal={eligibleTotal}
            onSelect={setSelected}
          />
        ))}
      </div>

      <BroadcastContactQuickViewModal
        recipient={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
